import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlansAdminService, Plan, CreatePlanInput, UpdatePlanInput, PlanContent } from "../services/plansAdminService";
import { SettingsAdminService } from "../services/settingsAdminService";
import { Plus, Trash2 } from "lucide-react";

interface PlanEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan?: Plan;
}

type BilingualItem = { en: string; pt: string };

const emptyBilingual = (): BilingualItem => ({ en: "", pt: "" });

// Backend rule (plans_validator.ts): slug = ^[a-z0-9-]+$, min 1 char.
const SLUG_PATTERN = /^[a-z0-9-]+$/;

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function BilingualFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BilingualItem;
  onChange: (v: BilingualItem) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          placeholder={t("settings.plansAdmin.editor.english")}
          value={value.en}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
        />
        <Input
          placeholder={t("settings.plansAdmin.editor.portuguese")}
          value={value.pt}
          onChange={(e) => onChange({ ...value, pt: e.target.value })}
        />
      </div>
    </div>
  );
}

function ItemListEditor({
  label,
  items,
  onChange,
}: {
  label: string;
  items: BilingualItem[];
  onChange: (items: BilingualItem[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, emptyBilingual()])}>
          <Plus className="h-3 w-3 mr-1" /> {t("settings.plansAdmin.editor.add")}
        </Button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input
            placeholder={t("settings.plansAdmin.editor.english")}
            value={item.en}
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...item, en: e.target.value };
              onChange(next);
            }}
          />
          <Input
            placeholder={t("settings.plansAdmin.editor.portuguese")}
            value={item.pt}
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...item, pt: e.target.value };
              onChange(next);
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-destructive hover:text-destructive"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function PlanEditorDialog({ open, onOpenChange, plan }: PlanEditorDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEditing = !!plan;

  // Form state
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [sortOrder, setSortOrder] = useState<number | "">("");
  const [isActive, setIsActive] = useState(true);
  const [content, setContent] = useState<PlanContent>({});
  // Once the user edits the slug (or we're editing an existing plan), stop deriving it from the name.
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({});
  // Required fields live on the "general" tab; track the active tab so a failed save can jump
  // back to it — otherwise the field highlights sit on a tab the user can't see (bug 83923).
  const [activeTab, setActiveTab] = useState("general");
  // Load existing plan data
  useEffect(() => {
    if (plan) {
      setName(plan.name);
      setSlug(plan.slug);
      setSortOrder(plan.sort_order ?? "");
      setIsActive(plan.is_active);
      setContent(plan.content ?? {});
      setSlugTouched(true);
    } else {
      setName("");
      setSlug("");
      setSortOrder("");
      setIsActive(true);
      setContent({});
      setSlugTouched(false);
    }
    setErrors({});
    setActiveTab("general");
  }, [plan, open]);

  // Load available contacts-list settings for permission_list select
  const { data: allSettings } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => SettingsAdminService.getSettings(),
    enabled: open,
  });
  const contactsLists = (allSettings ?? []).filter(
    (s) => (s.fields as { type?: string })?.type === "contacts-list"
  );

  const patchContent = (patch: Partial<PlanContent>) =>
    setContent((prev) => ({ ...prev, ...patch }));

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name, slug, content, sort_order: sortOrder === "" ? undefined : sortOrder, is_active: isActive };
      if (isEditing) {
        return PlansAdminService.updatePlan(plan!.id, payload as UpdatePlanInput);
      }
      return PlansAdminService.createPlan(payload as CreatePlanInput);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      onOpenChange(false);
    },
  });

  const validate = (): boolean => {
    const next: { name?: string; slug?: string } = {};
    if (!name.trim()) next.name = t("settings.plansAdmin.editor.errors.nameRequired");
    if (!slug.trim()) next.slug = t("settings.plansAdmin.editor.errors.slugRequired");
    else if (!SLUG_PATTERN.test(slug)) next.slug = t("settings.plansAdmin.editor.errors.slugInvalid");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      // Bring the required fields (and their error highlights) back into view.
      setActiveTab("general");
      return;
    }
    mutation.mutate();
  };

  // Field errors are cleared by setting the value to undefined (keys survive), so check
  // values — a key count would keep the summary visible after the user fixes the fields.
  const hasValidationErrors = Boolean(errors.name || errors.slug);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{isEditing ? t("settings.plansAdmin.editor.editTitle", { name: plan!.name }) : t("settings.plansAdmin.editor.newTitle")}</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="general">{t("settings.plansAdmin.editor.tabs.general")}</TabsTrigger>
              <TabsTrigger value="features">{t("settings.plansAdmin.editor.tabs.features")}</TabsTrigger>
              <TabsTrigger value="limits">{t("settings.plansAdmin.editor.tabs.limits")}</TabsTrigger>
              <TabsTrigger value="access">{t("settings.plansAdmin.editor.tabs.access")}</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-4 pr-1">
              {/* ── General ── */}
              <TabsContent value="general" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="plan-name">{t("settings.plansAdmin.editor.name")}</Label>
                    <Input
                      id="plan-name"
                      value={name}
                      error={!!errors.name}
                      aria-invalid={!!errors.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setName(value);
                        if (!slugTouched) setSlug(slugify(value));
                        if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                    />
                    {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="plan-slug">{t("settings.plansAdmin.editor.slug")}</Label>
                    <Input
                      id="plan-slug"
                      value={slug}
                      error={!!errors.slug}
                      aria-invalid={!!errors.slug}
                      onChange={(e) => {
                        setSlug(e.target.value);
                        setSlugTouched(true);
                        if (errors.slug) setErrors((prev) => ({ ...prev, slug: undefined }));
                      }}
                      placeholder={t("settings.plansAdmin.editor.slugPlaceholder")}
                    />
                    {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="sort-order">{t("settings.plansAdmin.editor.sortOrder")}</Label>
                    <Input
                      id="sort-order"
                      type="number"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value === "" ? "" : Number(e.target.value))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch id="is-active" checked={isActive} onCheckedChange={setIsActive} />
                    <Label htmlFor="is-active">{t("settings.plansAdmin.editor.active")}</Label>
                  </div>
                </div>
                <BilingualFields
                  label={t("settings.plansAdmin.editor.description")}
                  value={content.description ?? emptyBilingual()}
                  onChange={(v) => patchContent({ description: v })}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="billing-cycle">{t("settings.plansAdmin.editor.billingCycle")}</Label>
                    <Select
                      value={content.billing_cycle ?? ""}
                      onValueChange={(v) => patchContent({ billing_cycle: v as PlanContent["billing_cycle"] })}
                    >
                      <SelectTrigger id="billing-cycle">
                        <SelectValue placeholder={t("settings.plansAdmin.editor.billingSelectPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">{t("settings.plansAdmin.editor.billingMonthly")}</SelectItem>
                        <SelectItem value="semi-annual">{t("settings.plansAdmin.editor.billingSemiAnnual")}</SelectItem>
                        <SelectItem value="annual">{t("settings.plansAdmin.editor.billingAnnual")}</SelectItem>
                        <SelectItem value="lifetime">{t("settings.plansAdmin.editor.billingLifetime")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="trial-days">{t("settings.plansAdmin.editor.trialDays")}</Label>
                    <Input
                      id="trial-days"
                      type="number"
                      min={0}
                      value={content.trial_days ?? ""}
                      onChange={(e) => patchContent({ trial_days: e.target.value === "" ? undefined : Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="price-usd">{t("settings.plansAdmin.editor.priceUsd")}</Label>
                    <Input
                      id="price-usd"
                      type="number"
                      min={0}
                      step={0.01}
                      value={content.price_usd ?? ""}
                      onChange={(e) => patchContent({ price_usd: e.target.value === "" ? undefined : Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="price-brl">{t("settings.plansAdmin.editor.priceBrl")}</Label>
                    <Input
                      id="price-brl"
                      type="number"
                      min={0}
                      step={0.01}
                      value={content.price_brl ?? ""}
                      onChange={(e) => patchContent({ price_brl: e.target.value === "" ? undefined : Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="invoice"
                    checked={content.invoice === 1}
                    onCheckedChange={(v) => patchContent({ invoice: v ? 1 : 0 })}
                  />
                  <Label htmlFor="invoice">{t("settings.plansAdmin.editor.invoiceEnabled")}</Label>
                </div>
              </TabsContent>

              {/* ── Features & Metadata ── */}
              <TabsContent value="features" className="space-y-4 mt-0">
                <ItemListEditor
                  label={t("settings.plansAdmin.editor.features")}
                  items={content.features ?? []}
                  onChange={(v) => patchContent({ features: v })}
                />
                <ItemListEditor
                  label={t("settings.plansAdmin.editor.restrictions")}
                  items={content.metadata?.restrictions ?? []}
                  onChange={(v) => patchContent({ metadata: { ...content.metadata, restrictions: v } })}
                />
                <BilingualFields
                  label={t("settings.plansAdmin.editor.badge")}
                  value={content.metadata?.badge ?? emptyBilingual()}
                  onChange={(v) => patchContent({ metadata: { ...content.metadata, badge: v } })}
                />
                <BilingualFields
                  label={t("settings.plansAdmin.editor.ctaText")}
                  value={content.metadata?.cta_text ?? emptyBilingual()}
                  onChange={(v) => patchContent({ metadata: { ...content.metadata, cta_text: v } })}
                />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="meta-color">{t("settings.plansAdmin.editor.color")}</Label>
                    <Input
                      id="meta-color"
                      value={content.metadata?.color ?? ""}
                      onChange={(e) => patchContent({ metadata: { ...content.metadata, color: e.target.value } })}
                      placeholder={t("settings.plansAdmin.editor.colorPlaceholder")}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      id="meta-popular"
                      checked={content.metadata?.popular ?? false}
                      onCheckedChange={(v) => patchContent({ metadata: { ...content.metadata, popular: v } })}
                    />
                    <Label htmlFor="meta-popular">{t("settings.plansAdmin.editor.popular")}</Label>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      id="meta-highlight"
                      checked={content.metadata?.highlight ?? false}
                      onCheckedChange={(v) => patchContent({ metadata: { ...content.metadata, highlight: v } })}
                    />
                    <Label htmlFor="meta-highlight">{t("settings.plansAdmin.editor.highlight")}</Label>
                  </div>
                </div>
              </TabsContent>

              {/* ── Limits & Credits ── */}
              <TabsContent value="limits" className="space-y-4 mt-0">
                <div className="space-y-1">
                  <Label htmlFor="credits-limit">{t("settings.plansAdmin.editor.creditsLimit")}</Label>
                  <Input
                    id="credits-limit"
                    type="number"
                    value={content.credits?.limit ?? ""}
                    onChange={(e) =>
                      patchContent({
                        credits: {
                          ...content.credits,
                          limit: e.target.value === "" ? null : Number(e.target.value),
                        },
                      })
                    }
                    placeholder={t("settings.plansAdmin.editor.creditsLimitPlaceholder")}
                  />
                </div>
                <BilingualFields
                  label={t("settings.plansAdmin.editor.creditsLabel")}
                  value={content.credits?.label ?? emptyBilingual()}
                  onChange={(v) => patchContent({ credits: { ...content.credits, label: v } })}
                />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.plansAdmin.editor.creditUnitCosts")}</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const key = `unit_${Date.now()}`;
                        patchContent({
                          credits: {
                            ...content.credits,
                            unit_costs: {
                              ...(content.credits?.unit_costs ?? {}),
                              [key]: { cost: 1, label: emptyBilingual() },
                            },
                          },
                        });
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" /> {t("settings.plansAdmin.editor.add")}
                    </Button>
                  </div>
                  {Object.entries(content.credits?.unit_costs ?? {}).map(([key, uc]) => (
                    <div key={key} className="grid grid-cols-4 gap-2 items-center">
                      <Input
                        placeholder={t("settings.plansAdmin.editor.keyPlaceholder")}
                        defaultValue={key}
                        onBlur={(e) => {
                          if (e.target.value === key) return;
                          const { [key]: val, ...rest } = content.credits?.unit_costs ?? {};
                          patchContent({ credits: { ...content.credits, unit_costs: { ...rest, [e.target.value]: val } } });
                        }}
                      />
                      <Input
                        type="number"
                        placeholder={t("settings.plansAdmin.editor.costPlaceholder")}
                        value={uc.cost}
                        onChange={(e) =>
                          patchContent({
                            credits: {
                              ...content.credits,
                              unit_costs: {
                                ...(content.credits?.unit_costs ?? {}),
                                [key]: { ...uc, cost: Number(e.target.value) },
                              },
                            },
                          })
                        }
                      />
                      <Input
                        placeholder={t("settings.plansAdmin.editor.labelEnPlaceholder")}
                        value={uc.label.en}
                        onChange={(e) =>
                          patchContent({
                            credits: {
                              ...content.credits,
                              unit_costs: {
                                ...(content.credits?.unit_costs ?? {}),
                                [key]: { ...uc, label: { ...uc.label, en: e.target.value } },
                              },
                            },
                          })
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          const { [key]: _, ...rest } = content.credits?.unit_costs ?? {};
                          patchContent({ credits: { ...content.credits, unit_costs: rest } });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.plansAdmin.editor.fixedLimits")}</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const key = `limit_${Date.now()}`;
                        patchContent({
                          fixed_limits: {
                            ...(content.fixed_limits ?? {}),
                            [key]: { max: null, label: emptyBilingual(), unlimited: false },
                          },
                        });
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" /> {t("settings.plansAdmin.editor.add")}
                    </Button>
                  </div>
                  {Object.entries(content.fixed_limits ?? {}).map(([key, fl]) => (
                    <div key={key} className="grid grid-cols-4 gap-2 items-center">
                      <Input
                        placeholder={t("settings.plansAdmin.editor.keyPlaceholder")}
                        defaultValue={key}
                        onBlur={(e) => {
                          if (e.target.value === key) return;
                          const { [key]: val, ...rest } = content.fixed_limits ?? {};
                          patchContent({ fixed_limits: { ...rest, [e.target.value]: val } });
                        }}
                      />
                      <Input
                        type="number"
                        placeholder={t("settings.plansAdmin.editor.maxPlaceholder")}
                        value={fl.max ?? ""}
                        onChange={(e) =>
                          patchContent({
                            fixed_limits: {
                              ...(content.fixed_limits ?? {}),
                              [key]: { ...fl, max: e.target.value === "" ? null : Number(e.target.value) },
                            },
                          })
                        }
                      />
                      <Input
                        placeholder={t("settings.plansAdmin.editor.labelEnPlaceholder")}
                        value={fl.label.en}
                        onChange={(e) =>
                          patchContent({
                            fixed_limits: {
                              ...(content.fixed_limits ?? {}),
                              [key]: { ...fl, label: { ...fl.label, en: e.target.value } },
                            },
                          })
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          const { [key]: _, ...rest } = content.fixed_limits ?? {};
                          patchContent({ fixed_limits: rest });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Access Control ── */}
              <TabsContent value="access" className="space-y-4 mt-0">
                <div className="space-y-1">
                  <Label htmlFor="permission-list">{t("settings.plansAdmin.editor.permissionList")}</Label>
                  <Select
                    value={content.permission_list ?? "__none__"}
                    onValueChange={(v) => patchContent({ permission_list: v === "__none__" ? null : v })}
                  >
                    <SelectTrigger id="permission-list">
                      <SelectValue placeholder={t("settings.plansAdmin.editor.permissionListNone")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("settings.plansAdmin.editor.permissionListNone")}</SelectItem>
                      {contactsLists.map((s) => (
                        <SelectItem key={s.type} value={s.type}>
                          {(s.fields as { label?: { en?: string } })?.label?.en ?? s.type}
                          <span className="ml-2 text-xs text-muted-foreground">{s.type}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("settings.plansAdmin.editor.permissionListHint")}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="integrations">{t("settings.plansAdmin.editor.integrationsKey")}</Label>
                  <Input
                    id="integrations"
                    value={content.integrations ?? ""}
                    onChange={(e) => patchContent({ integrations: e.target.value || null })}
                    placeholder={t("settings.plansAdmin.editor.integrationsKeyPlaceholder")}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.plansAdmin.editor.integrationsKeyHint")}
                  </p>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          {hasValidationErrors && (
            <p className="text-xs text-destructive px-1">
              {t("settings.plansAdmin.editor.errors.summary")}
            </p>
          )}

          {mutation.isError && (
            <p className="text-xs text-destructive px-1">
              {(mutation.error as Error)?.message ?? t("settings.plansAdmin.editor.saveError")}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("settings.plansAdmin.editor.cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={mutation.isPending}>
              {mutation.isPending ? t("settings.plansAdmin.editor.saving") : isEditing ? t("settings.plansAdmin.editor.saveChanges") : t("settings.plansAdmin.editor.createPlan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
